import { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  useCategoryShelfV1RankCategoriesPlatformShelfGet,
  useKeywordShelfV1RankKeywordsKeywordShelfGet,
  useCategoryShelfRunsV1RankCategoriesPlatformShelfRunsGet,
  useKeywordShelfRunsV1RankKeywordsKeywordRunsGet,
} from "../api/generated/qcsControlPlane";
import type { PlacementOut, Platform, ShelfOut, ShelfProbeOut } from "../api/generated/model";
import { Flow } from "../api/generated/model";
import { ago, fmtDateTimeSec } from "../api/labels";
import { ApiError } from "../api/mutator";
import { platformFromKey, platformKeysFor, platformLabel } from "../api/platforms";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { useBreadcrumbTail } from "../components/breadcrumbs";
import { Badge, EmptyState, ErrorState, Panel, PageHeader, Skeleton, Stack } from "@akaike/ui";
import { Seg, Stat } from "../components/foundation";

/**
 * One shelf, as one run measured it.
 *
 * The page is the shelf itself and not a share of it: every placement the platform served,
 * competitors included, with the raw brand label it put on the card. Attribution to our own
 * brands is deliberately absent -- that question belongs to Q-Track, which reads these same
 * tables. See docs/scrape/adr/0008-the-shelf-page-attributes-no-brand.md.
 */

// Five decimals, about a metre. Ranking is a function of where the request came from, so
// probe points are what the page counts, and two anchors a metre apart are the same
// vantage point rather than two.
const COORD_DP = 5;

// Outcomes that mean the platform answered. An empty shelf is a measurement; a refused one
// is not, and holding the two apart is the whole reason the probe ledger exists.
const ANSWERED = new Set(["ok", "no_results", "unknown_category"]);

type Kind = "search" | "category";

type Located = { requested_latitude: number; requested_longitude: number };

/** One vantage point, with everything one run recorded from it. */
type Location = {
  key: string;
  latitude: number;
  longitude: number;
  storeIds: string[];
  servingStoreIds: string[];
  probes: ShelfProbeOut[];
  placements: PlacementOut[];
  answered: boolean;
  returnedShelf: boolean;
};

/**
 * One row of the distribution: a product AND whether the slot was paid for.
 *
 * Split on sponsorship because a product can hold a paid position on one shelf and an
 * organic one on another, and a median across the two describes a shelf nobody was served.
 */
type Distributed = {
  key: string;
  product_id: string;
  brand: string;
  product_name: string;
  is_sponsored: boolean;
  ranks: number[];
  locations: number;
  median: number;
  best: number;
  worst: number;
};

function at({ requested_latitude, requested_longitude }: Located): string {
  return `${requested_latitude.toFixed(COORD_DP)},${requested_longitude.toFixed(COORD_DP)}`;
}

function median(sorted: number[]): number {
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))].sort();
}

/** Group one run's probes and placements by the point they were asked from. */
function locate(probes: ShelfProbeOut[], placements: PlacementOut[]): Location[] {
  const byKey = new Map<string, Location>();
  const blank = (row: Located): Location => ({
    key: at(row),
    latitude: row.requested_latitude,
    longitude: row.requested_longitude,
    storeIds: [],
    servingStoreIds: [],
    probes: [],
    placements: [],
    answered: false,
    returnedShelf: false,
  });
  for (const probe of probes) {
    const loc = byKey.get(at(probe)) ?? blank(probe);
    loc.probes.push(probe);
    loc.answered ||= ANSWERED.has(probe.outcome);
    loc.returnedShelf ||= probe.outcome === "ok";
    byKey.set(loc.key, loc);
  }
  for (const placement of placements) {
    // A placement with no probe row cannot happen -- the ledger is written last, once the
    // chain finished -- but rendering one under its own location beats dropping it, which
    // would be a shelf position the page silently declined to show.
    const loc = byKey.get(at(placement)) ?? blank(placement);
    loc.placements.push(placement);
    byKey.set(loc.key, loc);
  }
  for (const loc of byKey.values()) {
    loc.storeIds = unique([
      ...loc.probes.map((p) => p.requested_store_id),
      ...loc.placements.map((p) => p.requested_store_id),
    ]);
    loc.servingStoreIds = unique([
      ...loc.probes.map((p) => p.store_id),
      ...loc.placements.map((p) => p.store_id),
    ]);
  }
  return [...byKey.values()].sort((a, b) => a.storeIds.join().localeCompare(b.storeIds.join()));
}

/** The per-product distribution across the locations that returned a shelf. */
function distribute(locations: Location[]): Distributed[] {
  const byKey = new Map<string, Distributed>();
  const seen = new Map<string, Set<string>>();
  for (const loc of locations) {
    for (const p of loc.placements) {
      const key = `${p.product_id}|${p.is_sponsored ? "paid" : "organic"}`;
      const row = byKey.get(key) ?? {
        key,
        product_id: p.product_id,
        brand: p.brand,
        product_name: p.product_name,
        is_sponsored: p.is_sponsored,
        ranks: [],
        locations: 0,
        median: 0,
        best: 0,
        worst: 0,
      };
      row.ranks.push(p.rank);
      byKey.set(key, row);
      const places = seen.get(key) ?? new Set<string>();
      places.add(loc.key);
      seen.set(key, places);
    }
  }
  return [...byKey.values()]
    .map((row) => {
      const sorted = [...row.ranks].sort((a, b) => a - b);
      return {
        ...row,
        locations: seen.get(row.key)?.size ?? 0,
        median: median(sorted),
        best: sorted[0],
        worst: sorted[sorted.length - 1],
      };
    })
    .sort((a, b) => a.median - b.median || Number(a.is_sponsored) - Number(b.is_sponsored));
}

/**
 * The shelf split by what the platform actually searched.
 *
 * A term the platform silently rewrote is an `ok` probe about a DIFFERENT shelf, so those
 * locations are their own block rather than being averaged into the distribution of the
 * term we asked for.
 */
function splitByRewrite(shelf: ShelfOut, locations: Location[]): { term: string; locs: Location[] }[] {
  const groups = new Map<string, Location[]>();
  for (const loc of locations) {
    const rewrite = loc.probes.find((p) => p.actual_term && p.actual_term !== shelf.term);
    const term = rewrite ? rewrite.actual_term : shelf.term;
    groups.set(term, [...(groups.get(term) ?? []), loc]);
  }
  return [...groups.entries()]
    .map(([term, locs]) => ({ term, locs }))
    .sort((a, b) => Number(b.term === shelf.term) - Number(a.term === shelf.term));
}

function errMsg(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === "string" && m) return m;
  }
  return fallback;
}

function Coordinate({ loc, to }: { loc: Location; to?: string }) {
  const label = loc.storeIds.join(", ") || "unknown store";
  return (
    <Stack className="gap-px">
      {to ? <Link className="text-brand no-underline hover:underline underline-offset-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand" to={to}>{label}</Link> : <span className="strong">{label}</span>}
      <code className="text-[11px] text-ink-2" title="The anchor we planted to be routed here">
        {loc.latitude.toFixed(COORD_DP)}, {loc.longitude.toFixed(COORD_DP)}
      </code>
    </Stack>
  );
}

function Outcome({ probe }: { probe: ShelfProbeOut }) {
  if (probe.outcome === "ok") return <Badge variant="outline-success">ok</Badge>;
  if (ANSWERED.has(probe.outcome)) return <Badge variant="outline-warning">{probe.outcome}</Badge>;
  return (
    <Badge variant="outline-destructive" title={probe.error || undefined}>
      {probe.outcome}
    </Badge>
  );
}

/** The ordered page one location was served, exactly as it came back. */
function LocationShelf({ loc, backTo }: { loc: Location; backTo: string }) {
  const columns = useMemo<AnyColumnDef<PlacementOut>[]>(() => {
    const col = createColumnHelper<PlacementOut>();
    return [
      col.accessor("rank", {
        header: "#",
        cell: ({ getValue }) => <span className="num strong">{getValue()}</span>,
        meta: { align: "right", width: 60 },
      }),
      col.accessor("product_name", {
        header: "Product",
        meta: { cellClassName: "strong", minWidth: 280 },
      }),
      col.accessor("brand", {
        header: "Brand",
        cell: ({ getValue }) => getValue() || <span className="text-[11px] text-ink-2">-</span>,
        meta: { minWidth: 160 },
      }),
      col.accessor((row) => (row.is_sponsored ? "sponsored" : "organic"), {
        id: "kind",
        header: "Kind",
        cell: ({ getValue }) =>
          getValue() === "sponsored" ? (
            <Badge variant="outline-warning">sponsored</Badge>
          ) : (
            <span className="text-[11px] text-ink-2">organic</span>
          ),
        meta: { width: 130, filterSelect: true, filterLabel: "kind" },
      }),
      col.accessor("product_id", {
        header: "Product id",
        cell: ({ getValue }) => <code>{getValue()}</code>,
        meta: { minWidth: 150 },
      }),
    ];
  }, []);
  const ordered = useMemo(
    () => [...loc.placements].sort((a, b) => a.rank - b.rank),
    [loc.placements],
  );
  const serving = loc.servingStoreIds.join(", ");
  // The list this replaces is thousands of rows tall, so a reader who opened a location far
  // down it would otherwise be left staring at whatever the old scroll offset now points at.
  const opened = useRef<HTMLDivElement>(null);
  useEffect(() => {
    opened.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [loc.key]);
  return (
    <div ref={opened} className="mt-3.5">
      <Panel>
        <Panel.Head className="flex-wrap [&_[data-slot=panel-title]]:whitespace-normal" title={`Shelf at ${loc.storeIds.join(", ") || loc.key}`}>
          <span className="grow" />
          <Link className="text-brand no-underline hover:underline underline-offset-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand" to={backTo}>All locations</Link>
        </Panel.Head>
        <Panel.Body>
          <p className="mb-3.5 text-[11px] text-ink-2">
            One vantage point, one page, in the order a shopper would have seen it. Asked from{" "}
            <code>
              {loc.latitude.toFixed(COORD_DP)}, {loc.longitude.toFixed(COORD_DP)}
            </code>
            {serving ? (
              <>
                {" "}
                and answered by <strong>{serving}</strong>, which is the store the ranking is a
                fact about
              </>
            ) : null}
            .
          </p>
          {ordered.length === 0 ? (
            <EmptyState title="Nothing came back from here" compact>
              The probe finished without a single position. An <code>ok</code> probe with no
              placements is an empty shelf; any other outcome means nobody was served a page at
              all.
            </EmptyState>
          ) : (
            <DataTable
              tableKey="shelf-location"
              columns={columns}
              data={ordered}
              // The probe point, not the rank: a location is keyed on the coordinate, so two
              // stores planted on the same point share one and each brings its own rank 1.
              getRowId={(row) => `${row.requested_store_id}-${row.page}-${row.rank}-${row.product_id}`}
              globalPlaceholder="Search this page..."
              csvFilename={`shelf-${loc.storeIds.join("-") || loc.key}`}
              emptyMessage="No placements match."
            />
          )}
        </Panel.Body>
      </Panel>
    </div>
  );
}

/** Every location this run asked from, answered or not, and what each one returned. */
function Locations({
  locations,
  linkFor,
}: {
  locations: Location[];
  linkFor: (key: string) => string;
}) {
  const columns = useMemo<AnyColumnDef<Location>[]>(() => {
    const col = createColumnHelper<Location>();
    return [
      col.accessor((row) => row.storeIds.join(", "), {
        id: "where",
        header: "Asked from",
        cell: ({ row }) => <Coordinate loc={row.original} to={linkFor(row.original.key)} />,
        meta: { minWidth: 220 },
      }),
      col.accessor((row) => row.servingStoreIds.join(", "), {
        id: "served",
        header: "Answered by",
        cell: ({ getValue }) => getValue() || <span className="text-[11px] text-ink-2">nobody</span>,
        meta: { minWidth: 160 },
      }),
      col.accessor((row) => row.probes[0]?.outcome ?? "", {
        id: "outcome",
        header: "Outcome",
        cell: ({ row }) =>
          row.original.probes[0] ? (
            <Outcome probe={row.original.probes[0]} />
          ) : (
            <span className="text-[11px] text-ink-2">-</span>
          ),
        meta: { width: 150, filterSelect: true, filterLabel: "outcome" },
      }),
      col.accessor((row) => row.placements.length, {
        id: "placements",
        header: "Placements",
        cell: ({ getValue }) => <span className="num">{getValue()}</span>,
        meta: { align: "right", width: 120 },
      }),
      col.accessor((row) => row.probes[0]?.attempts ?? 0, {
        id: "attempts",
        header: "Asks spent",
        cell: ({ getValue }) => <span className="num">{getValue()}</span>,
        meta: { align: "right", width: 120 },
      }),
    ];
  }, [linkFor]);
  return (
    <DataTable
      tableKey="shelf-locations"
      columns={columns}
      data={locations}
      getRowId={(row) => row.key}
      globalPlaceholder="Search location..."
      csvFilename="shelf-locations"
      emptyMessage="No locations match."
    />
  );
}

/** The cross-location distribution: where each product sat, over every shelf served. */
function Distribution({
  locations,
  answered,
  tableKey,
}: {
  locations: Location[];
  answered: number;
  // Its own namespace per group. A rewritten shelf renders a second Distribution on the
  // same page, and two DataTables sharing a key share their sort/search/visibility state
  // in the URL and in localStorage -- so touching one wipes the other's.
  tableKey: string;
}) {
  const rows = useMemo(() => distribute(locations), [locations]);
  const columns = useMemo<AnyColumnDef<Distributed>[]>(() => {
    const col = createColumnHelper<Distributed>();
    return [
      col.accessor("product_name", {
        header: "Product",
        meta: { cellClassName: "strong", minWidth: 280 },
      }),
      col.accessor("brand", {
        header: "Brand",
        cell: ({ getValue }) => getValue() || <span className="text-[11px] text-ink-2">-</span>,
        meta: { minWidth: 160 },
      }),
      col.accessor((row) => (row.is_sponsored ? "sponsored" : "organic"), {
        id: "kind",
        header: "Kind",
        cell: ({ getValue }) =>
          getValue() === "sponsored" ? (
            <Badge variant="outline-warning">sponsored</Badge>
          ) : (
            <span className="text-[11px] text-ink-2">organic</span>
          ),
        meta: { width: 130, filterSelect: true, filterLabel: "kind" },
      }),
      col.accessor("median", {
        header: "Median rank",
        cell: ({ getValue }) => <span className="num strong">{getValue()}</span>,
        meta: { align: "right", width: 130 },
      }),
      col.accessor("best", {
        header: "Best",
        cell: ({ getValue }) => <span className="num">{getValue()}</span>,
        meta: { align: "right", width: 90 },
      }),
      col.accessor("worst", {
        header: "Worst",
        cell: ({ getValue }) => <span className="num">{getValue()}</span>,
        meta: { align: "right", width: 90 },
      }),
      // The denominator is the locations that RETURNED a shelf, never the ones we planned.
      // A refused probe is not evidence that a product was absent.
      col.accessor("locations", {
        header: "Present at",
        cell: ({ getValue }) => (
          <span className="num">
            {getValue()}
            <span className="text-[11px] text-ink-2"> / {answered}</span>
          </span>
        ),
        meta: { align: "right", width: 130 },
      }),
      col.accessor("product_id", {
        header: "Product id",
        cell: ({ getValue }) => <code>{getValue()}</code>,
        meta: { minWidth: 150 },
      }),
    ];
  }, [answered]);
  if (rows.length === 0) {
    return (
      <EmptyState title="No placements returned" compact>
        No product positions are available for these locations. Check the outcomes below to
        distinguish unanswered probes from empty shelves.
      </EmptyState>
    );
  }
  return (
    <DataTable
      tableKey={tableKey}
      columns={columns}
      data={rows}
      getRowId={(row) => row.key}
      globalPlaceholder="Search product or brand..."
      csvFilename="shelf-distribution"
      defaultHidden={["product_id"]}
      emptyMessage="No products match."
    />
  );
}

export function Shelf({ kind }: { kind: Kind }) {
  const params = useParams();
  const [search, setSearch] = useSearchParams();
  const { pathname } = useLocation();
  const isSearch = kind === "search";
  const platforms = platformKeysFor(isSearch ? Flow.search_rank : Flow.category_rank);
  // A category id is a platform fact and rides the path (`1237/940` is one address with a
  // slash in it, which is what the splat is for). A keyword is one series across platforms,
  // so the platform is a choice the reader makes and lives in the query string.
  const routePlatform = platformFromKey(params.platform ?? "");
  const queryPlatform = platformFromKey(search.get("platform") ?? "");
  // `undefined` is an unknown or unrunnable key; the page shows its empty state for it
  // and the queries below stay disabled, so no request carries it.
  const platform: Platform | undefined = isSearch
    ? queryPlatform && platforms.includes(queryPlatform)
      ? queryPlatform
      : platforms[0]
    : routePlatform;
  const requestPlatform = platform ?? platforms[0];
  const term = isSearch ? (params.keyword ?? "") : (params["*"] ?? "");
  const runId = search.get("run");
  const openAt = search.get("at");

  useBreadcrumbTail([{ label: term || "Shelf" }]);

  const setParam = (key: string, value: string | null) =>
    setSearch((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null) next.delete(key);
      else next.set(key, value);
      // A run's shelf and a location inside it are different subjects: changing the run
      // must not leave a location from the previous one selected.
      if (key === "run" || key === "platform") next.delete("at");
      // A run id belongs to ONE platform, so it cannot be carried across: the shelf read
      // 404s on a run the new platform never made, and the page reads as a keyword that
      // platform has never measured.
      if (key === "platform") next.delete("run");
      return next;
    });

  // Opening a location is a place in the app, not a widget state: it survives a reload and
  // a paste, so it is a link and reads like every other drill-in here.
  const locationLink = useCallback(
    (key: string | null) => {
      const next = new URLSearchParams(search);
      if (key === null) next.delete("at");
      else next.set("at", key);
      const query = next.toString();
      return query ? `${pathname}?${query}` : pathname;
    },
    [pathname, search],
  );

  // The generated client interpolates the keyword straight into the path, and a keyword
  // is free text the registry normalises without stripping punctuation. Unencoded, a `?`
  // or a `#` in a tracked term truncates the path into a query or a fragment and the read
  // asks for a different keyword than the one on screen.
  const termPath = encodeURIComponent(term);

  // This page with the run selection dropped, for the empty state a stale `?run=` lands on.
  const newestRunHref = useMemo(() => {
    const next = new URLSearchParams(search);
    next.delete("run");
    next.delete("at");
    const q = next.toString();
    return q ? `${pathname}?${q}` : pathname;
  }, [pathname, search]);

  const keywordShelf = useKeywordShelfV1RankKeywordsKeywordShelfGet(
    termPath,
    { platform: requestPlatform, ...(runId ? { run_id: runId } : {}) },
    { query: { enabled: isSearch && Boolean(term) && Boolean(platform), retry: false } },
  );
  const categoryShelf = useCategoryShelfV1RankCategoriesPlatformShelfGet(
    requestPlatform,
    { category_id: term, ...(runId ? { run_id: runId } : {}) },
    { query: { enabled: !isSearch && Boolean(term) && Boolean(platform), retry: false } },
  );
  const keywordRuns = useKeywordShelfRunsV1RankKeywordsKeywordRunsGet(
    termPath,
    { platform: requestPlatform },
    { query: { enabled: isSearch && Boolean(term) && Boolean(platform) } },
  );
  const categoryRuns = useCategoryShelfRunsV1RankCategoriesPlatformShelfRunsGet(
    requestPlatform,
    { category_id: term },
    { query: { enabled: !isSearch && Boolean(term) && Boolean(platform) } },
  );

  const query = isSearch ? keywordShelf : categoryShelf;
  const runsQuery = isSearch ? keywordRuns : categoryRuns;
  const shelf = query.data;
  // A shelf no run has measured is a 404, not a read failure, and the two want opposite
  // pages: one tells an operator to switch the term on and queue a run, the other offers a
  // Retry button for a read that will 404 again. `retry: false` on both shelf queries is
  // there because this answer is expected.
  const notMeasured = query.error instanceof ApiError && query.error.status === 404;
  const runs = runsQuery.data ?? [];
  // The run the page is DESCRIBING, which is only the newest one when nothing is picked.
  // Reading the timestamp off `runs[0]` prints the newest run's clock beside an older
  // run's id, so the header quietly disagrees with the shelf under it.
  const shownRun = runs.find((r) => r.run_id === shelf?.run_id);

  const locations = useMemo(
    () => (shelf ? locate(shelf.probes, shelf.placements) : []),
    [shelf],
  );
  const groups = useMemo(
    () => (shelf ? splitByRewrite(shelf, locations) : []),
    [shelf, locations],
  );
  const open = locations.find((loc) => loc.key === openAt);

  const askedCount = locations.length;
  const answeredCount = locations.filter((loc) => loc.answered).length;
  const shelvesCount = locations.filter((loc) => loc.returnedShelf).length;
  const servingCount = unique(locations.flatMap((loc) => loc.servingStoreIds)).length;

  const titlePath = (shelf?.label || term).split(" / ");
  const title = isSearch ? (shelf?.label || term) : titlePath[titlePath.length - 1];

  return (
    <div className="min-w-0">
      <PageHeader
        className="mb-3.5 items-start sm:items-start"
        titleClassName="wrap-anywhere"
        title={title}
        eyebrow={!isSearch && titlePath.length > 1 ? titlePath.slice(0, -1).join(" / ") : undefined}
        description={isSearch ? "Product rankings for this keyword across store locations." : (
          <>{platform ? platformLabel(platform) : "Unknown platform"} category shelf <span className="ml-0 flex flex-wrap gap-1.5 sm:ml-3 sm:inline-flex">Path <code>{term}</code></span></>
        )}
        action={isSearch && platforms.length > 1 ? (
          <Seg
            className="max-w-full overflow-x-auto"
            brand
            value={requestPlatform}
            onChange={(p) => setParam("platform", p)}
            options={platforms.map((p) => ({ value: p, label: platformLabel(p) }))}
          />
        ) : undefined}
      />

      {!platform ? (
        // No platform declares the flow, so `platforms[0]` is undefined and both
        // reads are gated off. Without this the page falls through to "Not measured yet"
        // and names the platform it was not measured on -- which is nothing, so the
        // sentence ends "on ." and blames a shelf for a capability nobody ships.
        <EmptyState title="No platform measures this shelf">
          No platform supports{" "}
          <code>{isSearch ? "search_rank" : "category_rank"}</code>, so there is nothing to
          ask. See <Link className="text-brand no-underline hover:underline underline-offset-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand" to="/platforms">Platforms</Link> for what each one can run.
        </EmptyState>
      ) : query.isLoading ? (
        <Skeleton className="h-40" />
      ) : query.error && !notMeasured ? (
        <ErrorState
          title="Could not load this shelf"
          message={errMsg(query.error, "The read failed.")}
          onRetry={() => void query.refetch()}
        />
      ) : !shelf && runId && runsQuery.isLoading ? (
        // The run picker is a second, independent read. A `?run=` link to a run that did
        // not measure this shelf 404s the shelf read, and if that lands first the branch
        // below decides on `runs.length === 0` and tells the operator to go and switch a
        // tracked term on -- for a shelf that is in fact measured, under another run --
        // then flips once the picker resolves. Waiting is the whole fix.
        //
        // ``isLoading`` and not ``isPending``: a DISABLED query is pending forever, so a
        // term that resolves to empty (the category route's splat, with nothing after the
        // platform) would hold this skeleton on screen rather than falling through.
        <Skeleton className="h-40" />
      ) : !shelf && runId && runs.length > 0 ? (
        // A 404 with runs on the picker is a run that did not measure THIS shelf, not a
        // shelf nothing has measured. The picker only renders beside a shelf, so without
        // this the operator is told to go and switch a tracked term on, with no way back
        // to a run that has it short of editing the URL.
        <EmptyState title="That run did not measure this shelf">
          The run this link names is not one of the recent runs that asked for this shelf
          on {platformLabel(platform)}. <Link className="text-brand no-underline hover:underline underline-offset-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand" to={newestRunHref}>Show the newest run</Link>{" "}
          that did.
        </EmptyState>
      ) : !shelf ? (
        <EmptyState title="Not measured yet">
          Nothing has asked for this shelf on {platformLabel(platform)}. Switch it on in{" "}
          <Link className="text-brand no-underline hover:underline underline-offset-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand" to="/rank">Rank</Link> and run a{" "}
          <code>{isSearch ? "search_rank" : "category_rank"}</code> job; the first run that
          includes it fills this page.
        </EmptyState>
      ) : (
        <>
          <Panel>
            <Panel.Head className="flex-wrap [&_[data-slot=panel-title]]:whitespace-normal" title="Run summary">
              <span className="grow" />
              {runs.length > 1 ? (
                <Seg
            className="max-w-full overflow-x-auto"
                  brand
                  value={shelf.run_id}
                  onChange={(id) => setParam("run", id)}
                  // Six of the twenty the picker is offered, plus the one being shown if
                  // it is not among them: a `?run=` link to an older run otherwise leaves
                  // the Seg with nothing selected and no way back to it.
                  options={[
                    ...runs.slice(0, 6),
                    ...(runs.slice(0, 6).some((r) => r.run_id === shelf.run_id)
                      ? []
                      : runs.filter((r) => r.run_id === shelf.run_id)),
                  ].map((r) => ({
                    value: r.run_id,
                    label: ago(r.fetched_at),
                  }))}
                />
              ) : null}
            </Panel.Head>
            <Panel.Body>
              <div className="mb-3 grid gap-1.5 text-xs [&>div]:flex [&>div]:min-w-0 [&>div]:items-baseline [&>div]:gap-3 [&>div>span]:basis-16 [&>div>span]:shrink-0 [&>div>span]:text-ink-2 [&_strong]:font-medium [&_a]:min-w-0 [&_a]:wrap-anywhere">
                <div><span>Measured</span><strong>{shownRun ? fmtDateTimeSec(shownRun.fetched_at) : "Time unavailable"}</strong></div>
                <div><span>Job</span><Link className="text-brand no-underline hover:underline underline-offset-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand" to={`/jobs/${shelf.job_id}`}><code>{shelf.job_id}</code></Link></div>
              </div>
              <div className="grid grid-cols-1 border-t border-line pt-3 sm:grid-cols-3">
                <Stat className="px-0 py-2.5 [&+.kpi]:border-t [&+.kpi]:border-line sm:px-3.5 sm:py-1 sm:first:pl-0 sm:[&+.kpi]:border-t-0 sm:[&+.kpi]:border-l [&_.value]:font-mono [&_.value]:text-2xl [&_.value]:tabular-nums [&_.foot]:text-ink-2"
                  label="Shelves returned"
                  value={`${shelvesCount.toLocaleString("en-IN")} / ${askedCount.toLocaleString("en-IN")}`}
                  foot={`${answeredCount} answered, ${askedCount - answeredCount} unanswered`}
                  tone={shelvesCount === askedCount ? "ok" : "warn"}
                />
                <Stat className="px-0 py-2.5 [&+.kpi]:border-t [&+.kpi]:border-line sm:px-3.5 sm:py-1 sm:first:pl-0 sm:[&+.kpi]:border-t-0 sm:[&+.kpi]:border-l [&_.value]:font-mono [&_.value]:text-2xl [&_.value]:tabular-nums [&_.foot]:text-ink-2"
                  label="Locations asked"
                  value={askedCount.toLocaleString("en-IN")}
                  foot={`${servingCount} distinct serving stores`}
                />
                <Stat className="px-0 py-2.5 [&+.kpi]:border-t [&+.kpi]:border-line sm:px-3.5 sm:py-1 sm:first:pl-0 sm:[&+.kpi]:border-t-0 sm:[&+.kpi]:border-l [&_.value]:font-mono [&_.value]:text-2xl [&_.value]:tabular-nums [&_.foot]:text-ink-2" label="Placements loaded" value={shelf.placements.length.toLocaleString("en-IN")} />
              </div>
              {shelf.truncated ? (
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3 text-xs text-ink-2" role="status">
                  <Badge variant="outline-warning">Partial data</Badge>
                  <span>Only the first {shelf.placements.length.toLocaleString("en-IN")} placements are loaded.
                    Rankings and presence below reflect this subset.</span>
                </div>
              ) : null}
            </Panel.Body>
          </Panel>

          {groups.map(({ term: actual, locs }) => (
            <Panel className="mt-3.5" key={actual}>
              <Panel.Head className="flex-wrap [&_[data-slot=panel-title]]:whitespace-normal"
                title={
                  actual === shelf.term
                    ? "Product rankings"
                    : `Rewritten by the platform to "${actual}"`
                }
              />
              <Panel.Body tight>
                <p className="m-0 px-3.5 pt-3 pb-1.5 text-xs text-ink-2">
                  {actual === shelf.term
                    ? "Rankings across locations, with sponsored and organic placements counted separately."
                    : "The platform rewrote this keyword. These results are kept separate from the original search."}
                </p>
                <details className="px-3.5 pb-1.5 text-xs text-ink-2 [&_p]:max-w-[90ch] [&_p]:leading-relaxed">
                  <summary className="w-fit cursor-pointer rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand">How presence is counted</summary>
                  <p>"Present at" counts locations whose returned shelf is included in the loaded data.
                    Unanswered probes and empty shelves are excluded. A product's sponsored and organic
                    positions are separate rows, each with its own median rank.</p>
                </details>
                <Distribution
                  locations={locs}
                  tableKey={
                    actual === shelf.term ? "shelf-distribution" : `shelf-distribution-${actual}`
                  }
                  // Locations whose placements are actually in this payload, not every
                  // location that returned a shelf. Under truncation the two differ, and the
                  // wider denominator would report every product as absent from locations
                  // whose page simply did not fit.
                  answered={
                    locs.filter((loc) => loc.returnedShelf && loc.placements.length > 0).length
                  }
                />
              </Panel.Body>
            </Panel>
          ))}

          {/* One panel, two subjects. Opening a location replaces the list rather than
              appending below it, so the click lands where the reader is looking instead of
              growing the page under a fold they cannot see. */}
          {open ? (
            <LocationShelf loc={open} backTo={locationLink(null)} />
          ) : (
            <Panel className="mt-3.5">
              <Panel.Head className="flex-wrap [&_[data-slot=panel-title]]:whitespace-normal" title="Locations" />
              <Panel.Body tight>
                <p className="m-0 px-3.5 pt-3 pb-1.5 text-xs text-ink-2">
                  Every requested location, including unanswered probes. Open a location to inspect its shelf.
                  Multiple locations can be served by the same store.
                </p>
                <Locations locations={locations} linkFor={locationLink} />
              </Panel.Body>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}
